import { createContext, useCallback, useContext, useState } from "react";
import type { Macro, Workflow } from "@/data";

export interface SessionWorkflow {
  id: string;
  name: string;
  version: string;
  createdAt: string;
  author: string;
  status: "Draft" | "Under Review" | "Released" | "Deprecated";
}

export interface AuditEntry {
  id: string;
  actionId: string;
  title: string;
  approver: string;
  ts: string;
  conversationId: string;
  summary: string;
  tier: 1 | 2 | 3;
}

interface ActionState {
  sessionWorkflows: SessionWorkflow[];
  sessionDraftWorkflows: Workflow[];
  sessionMacros: Macro[];
  auditLog: AuditEntry[];
  attachedDatasets: Set<string>;
  addWorkflow: (w: SessionWorkflow) => void;
  addDraftWorkflow: (w: Workflow) => void;
  addMacro: (m: Macro) => void;
  addAudit: (e: AuditEntry) => void;
  flagDatasetAttached: (key: string) => void;
  resetDemo: () => void;
}

const Ctx = createContext<ActionState | null>(null);

export function ActionStoreProvider({ children }: { children: React.ReactNode }) {
  const [sessionWorkflows, setSessionWorkflows] = useState<SessionWorkflow[]>([]);
  const [sessionDraftWorkflows, setSessionDraftWorkflows] = useState<Workflow[]>([]);
  const [sessionMacros, setSessionMacros] = useState<Macro[]>([]);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [attachedDatasets, setAttachedDatasets] = useState<Set<string>>(new Set());

  const addWorkflow = useCallback((w: SessionWorkflow) => {
    setSessionWorkflows((p) => (p.some((x) => x.id === w.id) ? p : [w, ...p]));
  }, []);
  const addDraftWorkflow = useCallback((w: Workflow) => {
    setSessionDraftWorkflows((p) => {
      const idx = p.findIndex((x) => x.id === w.id);
      if (idx === -1) return [w, ...p];
      const next = p.slice();
      next[idx] = w;
      return next;
    });
  }, []);
  const addMacro = useCallback((m: Macro) => {
    setSessionMacros((p) => {
      const idx = p.findIndex((x) => x.id === m.id);
      if (idx === -1) return [m, ...p];
      const next = p.slice();
      next[idx] = m;
      return next;
    });
  }, []);
  const addAudit = useCallback((e: AuditEntry) => setAuditLog((p) => [e, ...p]), []);
  const flagDatasetAttached = useCallback(
    (key: string) => setAttachedDatasets((p) => new Set(p).add(key)),
    [],
  );
  const resetDemo = useCallback(() => {
    setSessionWorkflows([]);
    setSessionDraftWorkflows([]);
    setSessionMacros([]);
    setAuditLog([]);
    setAttachedDatasets(new Set());
  }, []);

  return (
    <Ctx.Provider
      value={{
        sessionWorkflows,
        sessionDraftWorkflows,
        sessionMacros,
        auditLog,
        attachedDatasets,
        addWorkflow,
        addDraftWorkflow,
        addMacro,
        addAudit,
        flagDatasetAttached,
        resetDemo,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useActionStore() {
  const v = useContext(Ctx);
  if (!v) throw new Error("ActionStoreProvider missing");
  return v;
}
